"use client";
import {
  __async
} from "../../../chunk-MMUBH76A.js";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { CpslButton, CpslIcon, CpslQrCode, CpslSpinner, CpslText } from "@getpara/react-components";
import { CenteredText, ErrorContainer, ErrorIcon, InnerStepContainer, QRContainer, StepContainer } from "../common.js";
import { useEffect, useMemo } from "react";
import { useModalStore } from "../../stores/index.js";
import styled from "styled-components";
import { useExternalWallets } from "../../providers/ExternalWalletContext.js";
import { useCopyToClipboard } from "@getpara/react-common";
import { ModalStep } from "../../utils/steps.js";
import { isMobile, isTablet, WalletType } from "@getpara/web-sdk";
import { routeMobileExternalWallet } from "../../utils/routeMobileExternalWallet.js";
const ExternalWalletStep = () => {
  const [isCopied, copy] = useCopyToClipboard();
  const externalWalletError = useModalStore((state) => state.externalWalletError);
  const setStep = useModalStore((state) => state.setStep);
  const { connectExternalWallet, wallet, qrUri, walletDisplayHelpers } = useExternalWallets();
  useEffect(() => {
    routeMobileExternalWallet(qrUri);
  }, [qrUri]);
  const handleTryAgainClick = () => __async(void 0, null, function* () {
    yield connectExternalWallet(wallet);
  });
  const handleCopy = () => {
    copy(qrUri);
  };
  const Content = useMemo(() => {
    var _a, _b, _c;
    if (!wallet) {
      return null;
    }
    const isWalletConnect = wallet.id === "walletConnect";
    const isMobileWalletConnect = isMobile() && isWalletConnect;
    if (isMobileWalletConnect) {
      /* @__PURE__ */ jsx(InnerStepContainer, { children: /* @__PURE__ */ jsx(Text, { weight: "semiBold", children: "Continue in the WalletConnect modal." }) });
    }
    const { showExtension, showMobile, isSolanaMobileIOS } = walletDisplayHelpers;
    if (!showMobile && !showExtension || isSolanaMobileIOS && !wallet.installed) {
      const text = isSolanaMobileIOS ? "Solana wallets aren't available on mobile IOS browsers.\n\nPlease continue in the wallet app." : `${wallet.name} isn't supported on mobile devices.

Please choose another wallet or continue on desktop.`;
      return /* @__PURE__ */ jsx(InnerStepContainer, { children: /* @__PURE__ */ jsx(Text, { weight: "semiBold", children: text }) });
    }
    if (showExtension) {
      const isInstalled = wallet.installed;
      return /* @__PURE__ */ jsxs(InnerStepContainer, { children: [
        isInstalled && !(externalWalletError == null ? void 0 : externalWalletError.length) ? /* @__PURE__ */ jsx(CenteredText, { color: "contrast", weight: "semiBold", children: `Confirm connection request in the ${wallet.name} browser extension.` }) : /* @__PURE__ */ jsxs(ErrorContainer, { children: [
          /* @__PURE__ */ jsx(ErrorIcon, { icon: "alertCircle" }),
          /* @__PURE__ */ jsx(CpslText, { weight: "semiBold", color: "error", children: isInstalled ? externalWalletError == null ? void 0 : externalWalletError[0] : `${wallet.name} not detected` })
        ] }),
        /* @__PURE__ */ jsxs(
          CpslButton,
          {
            as: isInstalled ? "button" : "a",
            href: (_a = wallet.downloadUrl) != null ? _a : "",
            target: "_blank",
            variant: "secondary",
            onClick: handleTryAgainClick,
            children: [
              /* @__PURE__ */ jsx(CpslIcon, { slot: "start", icon: isInstalled ? "refresh" : "linkExternal" }),
              isInstalled ? "Try Again" : `Get ${wallet.name}`
            ]
          }
        )
      ] });
    }
    if (showMobile) {
      if (wallet.type === WalletType.SOLANA || isMobile() && !isTablet()) {
        const isInstalled = wallet.type !== WalletType.SOLANA || wallet.installed;
        return /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(InnerStepContainer, { children: !isInstalled && /* @__PURE__ */ jsx(CpslText, { weight: "semiBold", color: "error", children: `${wallet.name} not detected` }) }),
          wallet.id !== "walletConnect" && /* @__PURE__ */ jsxs(InnerStepContainer, { children: [
            /* @__PURE__ */ jsx(CpslButton, { onClick: () => routeMobileExternalWallet(qrUri), fullWidth: true, children: "Connect Wallet" }),
            /* @__PURE__ */ jsx(Link, { href: (_b = wallet.downloadUrl) != null ? _b : "", target: "_blank", children: /* @__PURE__ */ jsxs(ExternalButton, { variant: "secondary", children: [
              `Get ${wallet.name}`,
              /* @__PURE__ */ jsx(ExternalIcon, { icon: "linkExternal" })
            ] }) })
          ] })
        ] });
      }
      const openWCModal = () => __async(void 0, null, function* () {
        yield connectExternalWallet(wallet, true, true);
      });
      const GetWalletButton = /* @__PURE__ */ jsxs(ExternalButton, { variant: "secondary", onClick: isWalletConnect ? openWCModal : void 0, children: [
        `${isWalletConnect ? "Open" : "Get"} ${wallet.name}`,
        /* @__PURE__ */ jsx(ExternalIcon, { icon: "linkExternal" })
      ] });
      return /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsxs(InnerStepContainer, { children: [
          /* @__PURE__ */ jsx(CpslText, { weight: "semiBold", children: "Scan with your mobile device" }),
          /* @__PURE__ */ jsx(QRContainer, { children: !qrUri ? /* @__PURE__ */ jsx(CpslSpinner, { size: 100 }) : /* @__PURE__ */ jsx(CpslQrCode, { url: qrUri, imageSrc: wallet.iconUrl }) }),
          /* @__PURE__ */ jsxs(CpslButton, { size: "small", variant: "ghost", onClick: handleCopy, children: [
            /* @__PURE__ */ jsx(CpslIcon, { slot: "start", icon: isCopied ? "check" : "copy" }),
            isCopied ? "Copied" : "Copy Link"
          ] })
        ] }),
        /* @__PURE__ */ jsx(InnerStepContainer, { children: isWalletConnect ? /* @__PURE__ */ jsx(Fragment, { children: GetWalletButton }) : /* @__PURE__ */ jsx(Link, { href: (_c = wallet.downloadUrl) != null ? _c : "", target: "_blank", children: GetWalletButton }) })
      ] });
    }
  }, [wallet, walletDisplayHelpers, externalWalletError, qrUri]);
  useEffect(() => {
    if (!wallet) {
      setStep(ModalStep.AUTH_MAIN);
    }
  }, [wallet]);
  if (!wallet) {
    return null;
  }
  return /* @__PURE__ */ jsx(Container, { children: Content });
};
const Container = styled(StepContainer)`
  flex: 1;
  justify-content: space-between;
`;
const Text = styled(CenteredText)`
  white-space: pre-line;
`;
const ExternalButton = styled(CpslButton)`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  width: 100%;
  cursor: pointer;
  margin-top: 8px;
  text-decoration: none;
`;
const ExternalIcon = styled(CpslIcon)`
  --height: 20px;
  --width: 20px;
`;
const Link = styled.a`
  text-decoration: none;
`;
export {
  ExternalWalletStep
};
